import { useState } from 'react';
import { Button, Col, Form, Row } from 'react-bootstrap';
import { IoSendSharp, IoAttachSharp } from 'react-icons/io5';
import { ITicket } from '../../commons/interfaces/ticket.interface';
import useAuth from '../../hooks/auth.hook';
import ChatbotAPI from '../../services/chatbot.service';

interface IChatFooterProps {
  ticket: ITicket;
  sendMessage: (currentMessage: string, fileUrl?: string) => Promise<void>;
}

const chatbotAPI = new ChatbotAPI();

const ChatFooter = ({ ticket, sendMessage }: IChatFooterProps) => {
  const { user } = useAuth();
  const managerId = user?.id as string;

  const [currentMessage, setCurrentMessage] = useState<string>('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string | null>(null);
  const [isTextInputDisabled, setIsTextInputDisabled] = useState<boolean>(false);

  const canSendMessage = ticket.manager === managerId && ticket.status === 'EXECUTING';

  const handleSendMessage = async () => {
    if (!selectedFile && !currentMessage) {
      console.warn('No hay archivo ni mensaje para enviar');
      return;
    }

    let messageContent = currentMessage;

    if (selectedFile && !fileUrl) {
      const formData = new FormData();
      formData.append('file', selectedFile);

      try {
        const response = await chatbotAPI.uploadFile(formData);
        messageContent = response.url.url || '';
        setFileUrl(messageContent);
      } catch (error) {
        console.error('Error al subir el archivo:', error);
        return;
      }
    }

    if (!messageContent.trim()) {
      console.warn('El mensaje no puede estar vacío');
      return;
    }

    await sendMessage(messageContent);

    setCurrentMessage('');
    setSelectedFile(null);
    setFileUrl(null);
    setIsTextInputDisabled(false);
  };

  const handleFileChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
    if (ev.target.files && ev.target.files.length > 0) {
      const file = ev.target.files[0];

      setSelectedFile(null);
      setFileUrl(null);
      setIsTextInputDisabled(true);

      setTimeout(() => {
        setSelectedFile(file);
      }, 0);

      ev.target.value = '';
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (canSendMessage) {
        handleSendMessage();
      }
    }
  };

  return (
    <Row className="px-2 py-2 chat-footer">
      <Col className="ps-4">
        <Form.Group>
          <Form.Control
            type="text"
            placeholder="Mensaje"
            value={currentMessage}
            onChange={(ev) => setCurrentMessage(ev.target.value)}
            onKeyDown={handleKeyDown}
            disabled={!canSendMessage || isTextInputDisabled}
          />
        </Form.Group>
      </Col>

      {selectedFile && (
        <Col xs="auto">
          <p>{selectedFile.name}</p>
        </Col>
      )}

      <Col xs="auto">
        <Form.Group>
          <Form.Control type="file" onChange={handleFileChange} id="file-input" hidden />
          <Button
            variant="outline"
            as="label"
            htmlFor="file-input"
            disabled={!canSendMessage}
            className="d-flex align-items-center">
            <IoAttachSharp />
          </Button>
        </Form.Group>
      </Col>

      <Col className="pe-4" xs="auto">
        <Button variant="outline" onClick={handleSendMessage} disabled={!canSendMessage}>
          <IoSendSharp />
        </Button>
      </Col>
    </Row>
  );
};

export default ChatFooter;
